///
/// EarthBrowser
///
/// Created by Matt Giger on 1/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"
import type { NexradScan } from "@shared-types/nexrad"

export interface NexradScanProperties extends DatasetFeature {
	id: number
	station_id: string
	station_name: string
	lat: number
	lon: number
	elevation_m: number | null
	scan_time: string
	atlas_url: string
	grid_shape: string // JSON array as string
	bounds_km: string // JSON object as string
	max_reflectivity: number | null
	coverage_km: number
	ingested_at: string
}

/**
 * Grouped station feature with all its scans
 * Used as the feature type for scene objects (one per station)
 */
export interface NexradStationFeature extends DatasetFeature {
	id: string // station_id
	station_id: string
	station_name: string
	lat: number
	lon: number
	/** Site elevation in meters MSL; the scan grid's z=0 datum */
	elevation_m: number | null
	scans: NexradScan[]
}

const cBatchSize = 100

export class NexradTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists nexrad_scans (
					id integer primary key,
					station_id text not null,
					station_name text not null,
					lat real not null,
					lon real not null,
					elevation_m real,
					scan_time text not null,
					atlas_url text not null,
					grid_shape text not null,
					bounds_km text not null,
					max_reflectivity real,
					coverage_km real not null,
					ingested_at text not null,
					geom blob
				)`
			},
			{ sql: "create index if not exists idx_nexrad_scans_station_id on nexrad_scans(station_id)" },
			{ sql: "create index if not exists idx_nexrad_scans_scan_time on nexrad_scans(scan_time desc)" },
			{ sql: "create unique index if not exists idx_nexrad_scans_station_time on nexrad_scans(station_id, scan_time)" },

			{
				sql: `create virtual table if not exists nexrad_scans_idx using rtree(
					id,
					min_x, max_x,
					min_y, max_y
				)`
			},
			{
				sql: `create trigger if not exists nexrad_scans_idx_insert
				after insert on nexrad_scans
				begin
					insert into nexrad_scans_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists nexrad_scans_idx_update
				after update on nexrad_scans
				begin
					delete from nexrad_scans_idx where id = old.rowid;
					insert into nexrad_scans_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`
			},
			{
				sql: `create trigger if not exists nexrad_scans_idx_delete
				after delete on nexrad_scans
				begin
					delete from nexrad_scans_idx where id = old.rowid;
				end`
			},
		])
	}

	public static async replaceAll(rows: NexradScanProperties[]): Promise<void> {
		await Database.db.exec("delete from nexrad_scans", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: NexradScanProperties[]): Promise<void> {
		const statement = `insert into nexrad_scans (
				id, station_id, station_name, lat, lon, elevation_m,
				scan_time, atlas_url, grid_shape, bounds_km,
				max_reflectivity, coverage_km, ingested_at,
				geom
			) values (
				?, ?, ?, ?, ?, ?,
				?, ?, ?, ?,
				?, ?, ?,
				ST_SetSRID(ST_MakePoint(?, ?, ?), 4326)
			)
			on conflict(id) do update set
				station_id = excluded.station_id,
				station_name = excluded.station_name,
				lat = excluded.lat,
				lon = excluded.lon,
				elevation_m = excluded.elevation_m,
				scan_time = excluded.scan_time,
				atlas_url = excluded.atlas_url,
				grid_shape = excluded.grid_shape,
				bounds_km = excluded.bounds_km,
				max_reflectivity = excluded.max_reflectivity,
				coverage_km = excluded.coverage_km,
				ingested_at = excluded.ingested_at,
				geom = excluded.geom`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.id,
				r.station_id,
				r.station_name,
				r.lat,
				r.lon,
				r.elevation_m,
				r.scan_time,
				r.atlas_url,
				r.grid_shape,
				r.bounds_km,
				r.max_reflectivity,
				r.coverage_km,
				r.ingested_at,
				r.lon,
				r.lat,
				(r.elevation_m ?? 0),
			]))
		}
	}

	public static async deleteByIds(ids: number[]): Promise<void> {
		if (ids.length === 0) return
		const placeholders = ids.map(() => "?").join(",")
		await Database.db.exec(`delete from nexrad_scans where id in (${placeholders})`, ids, "critical")
	}

	public static async getAll(): Promise<NexradScanProperties[]> {
		const rows = await Database.db.exec(
			`select
				id, station_id, station_name, lat, lon, elevation_m,
				scan_time, atlas_url, grid_shape, bounds_km,
				max_reflectivity, coverage_km, ingested_at
			from nexrad_scans
			order by scan_time desc`,
			[],
			"low"
		)
		return rows as NexradScanProperties[]
	}

	public static async getLatestByStation(): Promise<NexradScanProperties[]> {
		const rows = await Database.db.exec(
			`select
				id, station_id, station_name, lat, lon, elevation_m,
				scan_time, atlas_url, grid_shape, bounds_km,
				max_reflectivity, coverage_km, ingested_at
			from nexrad_scans
			where (station_id, scan_time) in (
				select station_id, max(scan_time)
				from nexrad_scans
				group by station_id
			)
			order by station_name`,
			[],
			"low"
		)
		return rows as NexradScanProperties[]
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from nexrad_scans`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	public static async getByStationId(stationId: string): Promise<NexradScanProperties[]> {
		const rows = await Database.db.exec(
			`select
				id, station_id, station_name, lat, lon, elevation_m,
				scan_time, atlas_url, grid_shape, bounds_km,
				max_reflectivity, coverage_km, ingested_at
			from nexrad_scans
			where station_id = ?
			order by scan_time desc
			limit 10`,
			[stationId],
			"low"
		)
		return rows as NexradScanProperties[]
	}

	/**
	 * Get all scans grouped by station
	 * Returns one NexradStationFeature per unique station_id with all its scans
	 */
	public static async getStationsWithScans(): Promise<NexradStationFeature[]> {
		const rows = await Database.db.exec(
			`select
				id, station_id, station_name, lat, lon, elevation_m,
				scan_time, atlas_url, grid_shape, bounds_km,
				max_reflectivity, coverage_km, ingested_at
			from nexrad_scans
			order by station_id, scan_time desc`,
			[],
			"low"
		) as NexradScanProperties[]

		// Group by station_id
		const stationMap = new Map<string, NexradStationFeature>()

		for (const row of rows) {
			let station = stationMap.get(row.station_id)
			if (!station) {
				station = {
					id: row.station_id,
					station_id: row.station_id,
					station_name: row.station_name,
					lat: row.lat,
					lon: row.lon,
					elevation_m: row.elevation_m,
					scans: []
				}
				stationMap.set(row.station_id, station)
			}

			// Convert to NexradScan format
			station.scans.push({
				station_id: row.station_id,
				scan_time: row.scan_time,
				atlas_url: row.atlas_url,
				grid_shape: this.parseGridShape(row.grid_shape),
				bounds_km: this.parseBoundsKm(row.bounds_km),
				max_reflectivity: row.max_reflectivity,
				coverage_km: row.coverage_km
			})
		}

		return Array.from(stationMap.values())
	}

	private static parseGridShape(value: string): number[] {
		try {
			const parsed: unknown = JSON.parse(value)
			return Array.isArray(parsed) ? (parsed as number[]) : [192, 192, 96]
		} catch {
			return [192, 192, 96]
		}
	}

	private static parseBoundsKm(value: string): NexradScan["bounds_km"] {
		try {
			const parsed: unknown = JSON.parse(value)
			if (parsed && typeof parsed === "object" && "minX" in parsed) {
				return parsed as NexradScan["bounds_km"]
			}
			return null
		} catch {
			return null
		}
	}
}

